/**
 * Domain extraction + normalization for the demo-call lead → Close.com match.
 *
 * Close stores company websites on the lead's top-level `url` field (e.g.
 * "https://dbshome.de/"). To find an existing lead for a submitted website we
 * reduce both sides to a comparable host: lowercase, no protocol, no `www.`,
 * no path/query. Matching is exact on that host — Close's full-text search is
 * OR-tokenized and returns many non-matches, so the host comparison is what
 * actually decides a hit.
 */

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//;
/** Bare email — must not pass as website (`https://user@host` parses as URL userinfo). */
const EMAIL_LIKE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reduce any user-entered website to a bare host ("immobilien-winkelmann.de").
 * Accepts input with or without a scheme. Returns `null` when no plausible
 * host can be parsed (caller treats this as "no domain to match against").
 */
export function extractDomain(input: string | undefined | null): string | null {
  if (!input) return null;
  let value = input.trim().toLowerCase();
  if (value.length === 0) return null;
  if (EMAIL_LIKE_RE.test(value)) return null;
  if (value.startsWith("mailto:")) return null;
  if (!SCHEME_RE.test(value)) {
    value = `https://${value}`;
  }
  let host: string;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    if (parsed.username.length > 0 || parsed.password.length > 0) {
      return null;
    }
    host = parsed.hostname;
  } catch {
    return null;
  }
  host = host.replace(/^www\./, "").replace(/\.$/, "");
  // A real domain has at least one dot and no whitespace.
  if (!host.includes(".") || /\s/.test(host)) return null;
  return host;
}

/** Public demo form: website field must resolve to an http(s) host, not an email. */
export function isValidDemoLeadWebsite(input: string): boolean {
  return extractDomain(input) !== null;
}

/** True when two website strings resolve to the same host (www-insensitive). */
export function domainsMatch(
  a: string | undefined | null,
  b: string | undefined | null,
): boolean {
  const da = extractDomain(a);
  const db = extractDomain(b);
  return da !== null && db !== null && da === db;
}
